"use client";
import React, { useState } from "react";
import Image from "next/image";
import RequestForm from "./form";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade, Pagination } from "swiper/modules";

import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";

export interface BannerProps {
  data?: any;
  isMobile?: boolean;
}

const RequestProposalBanner: React.FC<BannerProps> = ({ data }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";

  return (
    <section>
      {/* Banner Section */}
      <div className="request-proposal-banner bg-background-dark  py-[120px] lg:py-[150px] xl:pb-[240px]  relative z-0">
        {/* Shapes */}
        <div className="banner-shape-1 absolute top-[0px] left-0 z-[1] w-[50%] md:w-[40%]">
          <Image
            src="/images/banner-left-layer-mobile.png"
            alt="Banner Shape"
            width={571}
            height={571}
            className="w-full h-full md:hidden"
          />
          <Image
            src="/images/banner-left-layer-tablet.png"
            alt="Banner Shape"
            width={571}
            height={571}
            className="w-full h-full hidden md:block lg:hidden"
          />
          <Image
            src="/images/banner-left-layer-desktop.png"
            alt="Banner Shape"
            width={571}
            height={571}
            className="w-full h-full hidden lg:block"
          />
        </div>
        <div className="banner-shape-1 absolute bottom-0 right-0 z-[-1] ">
          <Image
            src="/images/banner-right-layer-mobile.png"
            alt="Banner Shape"
            width={471}
            height={471}
            className="object-cover w-full h-full md:hidden"
          />
          <Image
            src="/images/banner-right-layer-tablet.png"
            alt="Banner Shape"
            width={471}
            height={471}
            className="object-cover w-full h-full hidden md:block lg:hidden"
          />
          <Image
            src="/images/banner-right-layer-desktop.png"
            alt="Banner Shape"
            width={471}
            height={471}
            className="object-cover w-full h-full hidden lg:block"
          />
        </div>

        <div className="container lg:p-[0px_24px]!">
          <div>
            <h1 className="text-white font-extrabold text-display text-center uppercase">
              {data?.title}
            </h1>
            <p className="text-secondary-text-white font-regular text-subtitle-3 text-center mt-4">
              {data?.description}
            </p>
          </div>
        </div>
      </div>

      {/* Form + Slider Section */}
      <div className="pb-[50px] md:pb-[80px] xl:pb-[100px] mt-[-100px] xl:mt-[-200px] relative z-0">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-[30%_70%] xl:grid-cols-[30%_70%] gap-[30px]">
            {/* Left Slider */}
            <div className="left-div relative order-2 lg:order-0">
              {/* Keen Slider Container */}
              <div className="relative h-full overflow-hidden ">
                <Swiper
                  effect="fade"
                  className="h-full"
                  modules={[Autoplay, EffectFade, Pagination]}
                  autoplay={{ delay: 3000, disableOnInteraction: false }}
                  onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
                >
                  {data?.services?.map((service: any, index: number) => (
                    <SwiperSlide key={"Swiper" + index}>
                      <div className="flex flex-col h-full relative ">
                        <div className="p-[16px] bg-[#E6F3FD] border-[#E6F3FD] border-[0.4px]">
                          <p className="text-[20px] leading-[26px] xl:text-[24px] xl:leading-[31px] font-bold text-center text-[#0086E6]">
                            {index + 1 < 10 ? `0${index + 1}` : index + 1}
                          </p>
                        </div>
                        <div className="middle-div relative bg-white z-[-2] min-h-[550px] sm:min-h-[650px] lg:min-h-[600px]">
                          <div className="top-layer absolute top-0 z-[-1] w-full h-[90%] xl:h-[80%]">
                            <Image
                              src="/images/hero-layer-top.png"
                              alt="Top Layer"
                              width={1000}
                              height={1000}
                              className="object-cover w-full h-[90%] xl:h-[80%]"
                            />
                          </div>
                          <div className="w-[83px] h-[83px] xl:w-[108px] xl:h-[108px] flex items-center justify-center bg-white mb-[50px] mt-[60px] mx-auto">
                            {service?.image?.data?.attributes?.url && (
                              <Image
                                src={
                                  baseUrl +
                                  service?.image?.data?.attributes?.url
                                }
                                alt="Step 3"
                                width={85}
                                height={85}
                              />
                            )}
                          </div>
                          <div className="px-[16px] sm:px-[30px] lg:px-[16px] xl:px-[30px] relative z-10">
                            <h2 className="text-center text-[22px] md:text-[24px] xl:text-[32px] font-[700] mb-[12px]">
                              {service?.short_title}
                            </h2>
                            <p className="text-center text-[18px] xl:text-[20px] text-[#040405]">
                              {service?.title}
                            </p>
                          </div>
                        </div>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
                <div className="bottom-layer absolute bottom-0 z-[1] w-full h-[50%] sm:h-[70%] lg:h-[50%]">
                  <Image
                    src="/images/hero-layer-bottm.png"
                    alt="Step 3"
                    width={1000}
                    height={1000}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Custom Pagination */}
                <div className="mt-[30px] lg:mt-[15px] flex justify-center items-center gap-2 px-12 py-6 sm:w-[67%] mx-auto relative sm:absolute bottom-[15%] lg:bottom-[20%] left-[50%] sm:transform -translate-x-[50%] z-[5]">
                  {[0, 1, 2].map((idx) => (
                    <div
                      key={idx}
                      className={`p-0 relative rounded overflow-hidden ${
                        activeIndex === idx
                          ? " flex w-[128px] h-1 bg-background"
                          : "flex-none w-[16px] h-1 bg-background"
                      }`}
                    >
                      <span
                        className={`absolute top-0 rounded left-0  h-1 ${
                          activeIndex === idx
                            ? "bg-gradient-to-r from-[#8ABC00] to-[#0086E6] transition-all duration-[4000ms] w-[128px] "
                            : "left-[-100%] w-[16px] "
                        }`}
                      ></span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="right-div flex items-center h-full order-1 lg:order-2 w-full">
              <div className="h-full w-full">
                <RequestForm />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RequestProposalBanner;
